import {
  convertFileToBase64,
  removeCpfMask,
  removeTelephoneCountryCode,
  removeZipCodeMask,
  STATUSPAGE,
} from '@repo/ui';
import { ConflictError, NotFoundError } from 'domain/errors';
import {
  CreateInternshipLocationUseCaseInterface,
  DeleteInternshipLocationImageUseCaseInterface,
  GetAddressViaZipCodeUseCaseInterface,
  GetAllCitiesForSelectOptionsUseCaseInterface,
  GetAllCountriesUseCaseInterface,
  GetCoordinatesFromAddressUseCaseInterface,
  GetInternshipLocationByIdUseCaseInterface,
  GetSearchByCnpjUseCaseInterface,
  UpdateInternshipLocationImageUseCaseInterface,
  UpdateInternshipLocationUseCaseInterface,
  VerifyInternshipResponsibleCpfUseCaseInterface,
  VerifyInternshipResponsibleEmailUseCaseInterface,
} from 'domain/usecases';
import { useFormik } from 'formik';
import { notifyError, notifySuccess } from 'presentation/app/components/notify';
import { useService } from 'presentation/app/hooks/useService';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import {
  clearSelectedInternshipLocationData,
  loadSelectedInternshipLocationData,
} from 'presentation/config/store/internship-locations/internship-locations-slice';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import {
  CreateOrUpdateInternshipLocationFormStructure,
  getCreateOrUpdateInternshipLocationFormInitialValues,
  getCreateOrUpdateInternshipLocationFormValidationSchema,
} from '../common/forms/create-or-update-internship-location-form';

const requestsStatusInitialState = {
  getAllCountries: STATUSPAGE.INITIAL,
  getCoordinatesFromAddress: STATUSPAGE.INITIAL,
  getInternshipLocationById: STATUSPAGE.INITIAL,
  createOrUpdate: STATUSPAGE.INITIAL,
  getAddressViaZipCode: STATUSPAGE.INITIAL,
  getAllCitiesForSelectOptions: STATUSPAGE.INITIAL,
  verifyInternshipResponsibleCpf: STATUSPAGE.INITIAL,
  verifyInternshipResponsibleEmail: STATUSPAGE.INITIAL,
  updateInternshipLocationImage: STATUSPAGE.INITIAL,
  deleteInternshipLocationImage: STATUSPAGE.INITIAL,
};

export const useCreateOrUpdateInternshipLocation = () => {
  // Hooks
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { internshipLocationsService, locationsService, searchByCnpjService } =
    useService();

  // States
  const { selectedInternshipLocationData } = useAppSelector(
    (state) => state.InternshipLocations,
  );
  const photoFileInputRef = useRef<HTMLInputElement>(null);
  const [photoFilePreviewUrl, setPhotoFilePreviewUrl] = useState<string>('');
  const [citiesForSelectOptionsData, setCitiesForSelectOptionsData] = useState(
    [],
  );
  const [countriesData, setCountriesData] =
    useState<GetAllCountriesUseCaseInterface.Result>([]);
  const [requestsStatus, setRequestsStatus] = useState<
    typeof requestsStatusInitialState
  >(requestsStatusInitialState);
  const [isConfirmDeleteImageModalOpen, setIsConfirmDeleteImageModalOpen] =
    useState(false);
  // Constants
  const areInputsAndBtnsDisabled =
    requestsStatus.getAllCountries === STATUSPAGE.LOADING ||
    requestsStatus.getInternshipLocationById === STATUSPAGE.LOADING ||
    requestsStatus.createOrUpdate === STATUSPAGE.LOADING;
  const zipCodeSchema = Yup.string()
    .zipCodeType('CEP inválido')
    .required('Campo é obrigatório');
  const cpfSchema = Yup.string().cpfType('CPF inválido');
  const emailSchema = Yup.string().email('E-mail inválido');
  const allowedPhotoFileTypes = ['image/png', 'image/jpg', 'image/jpeg'];

  //cnpj
  const autoFillAddressInputValuesToCnpjAPI = useCallback(
    ({
      cityName,
      cityId,
      street,
      zipCode,
      neighborhood,
    }: GetSearchByCnpjUseCaseInterface.Result) => {
      validation.setFieldValue('street', street || '');
      validation.setFieldValue('district', neighborhood || '');
      validation.setFieldValue('zipCode', zipCode || '');
      validation.setFieldValue(
        'city',
        cityName && cityId
          ? {
              label: cityName,
              value: Number(cityId),
            }
          : null,
      );
    },
    [],
  );

  // Methods
  const changeRequestStatus = useCallback(
    (key: keyof typeof requestsStatusInitialState, value: STATUSPAGE) => {
      setRequestsStatus((prevState) => {
        return {
          ...prevState,
          [key]: value,
        };
      });
    },
    [],
  );

  const toggleConfirmDeleteImageModal = useCallback(() => {
    setIsConfirmDeleteImageModalOpen((prevState) => !prevState);
  }, []);

  const handlePhotoInputChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      try {
        const uploadedFile = event.target.files?.[0];
        const maxAllowedSize = 5 * 1024 * 1024; // 5 MegaBytes
        if (
          !uploadedFile ||
          !(uploadedFile instanceof File) ||
          !allowedPhotoFileTypes.includes(uploadedFile.type) ||
          !(uploadedFile.size <= maxAllowedSize)
        ) {
          alert(
            'Apenas arquivos .png, .jpg e .jpeg são aceitos e devem ser menores que 5 MB',
          );
          return;
        }

        const base64UploadedFile = await convertFileToBase64(uploadedFile);

        return requestUpdateInternshipLocationImage({
          id,
          photo: base64UploadedFile,
        });
      } catch (err) {
        notifyError('Erro ao fazer upload da imagem');
      }
    },
    [],
  );

  const removeSelectedInternshipLocationData = useCallback(() => {
    dispatch(clearSelectedInternshipLocationData());
  }, []);

  const getFormattedAddress = useCallback(
    (params: {
      zipCode: string;
      street: string;
      district: string;
      cityAndFederalUnit: string;
      addressNumber?: string;
      country?: string;
    }): string => {
      const {
        street,
        district,
        cityAndFederalUnit,
        country,
        zipCode,
        addressNumber,
      } = params;
      const streetWithNumber = addressNumber
        ? `${street}, ${addressNumber}`
        : street;
      return `${streetWithNumber}, ${district}, ${cityAndFederalUnit}, ${country || 'Brasil'}, ${zipCode}`;
    },
    [],
  );

  const requestGetAllCountries = useCallback(async () => {
    changeRequestStatus('getAllCountries', STATUSPAGE.LOADING);
    await locationsService
      .getAllCountries()
      .then((res) => {
        setCountriesData(res);
        const brazilCountry = res.find((country) => country.id === 'BR');
        if (!id) {
          validation.setFieldValue(
            'country',
            brazilCountry
              ? {
                  value: brazilCountry.id,
                  label: brazilCountry.name,
                }
              : null,
          );
        }
      })
      .catch((err) => {
        setCountriesData([]);
      })
      .finally(() => {
        changeRequestStatus('getAllCountries', STATUSPAGE.DONE);
      });
  }, []);

  const requestCreateInternshipLocation = useCallback(
    async (params: CreateInternshipLocationUseCaseInterface.Params) => {
      changeRequestStatus('createOrUpdate', STATUSPAGE.LOADING);
      await internshipLocationsService
        .createInternshipLocation(params)
        .then((res) => {
          notifySuccess(res.message);

          if (!params.isRemote) {
            navigate(
              `/cadastros/locais-de-estagio/novo/confirmar-localizacao-do-marcador-no-mapa`,
              {
                state: {
                  id: 'confirm-marker-location',
                  value: {
                    id: res.id,
                    lat: params.lat,
                    lng: params.lng,
                    countryId: params.countryId,
                  },
                },
              },
            );
          } else {
            navigate('/cadastros/locais-de-estagio');
          }
        })
        .catch((err) => {
          notifyError(err.message);
        })
        .finally(() => {
          changeRequestStatus('createOrUpdate', STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestGetInternshipLocationById = useCallback(
    async (params: GetInternshipLocationByIdUseCaseInterface.Params) => {
      changeRequestStatus('getInternshipLocationById', STATUSPAGE.LOADING);
      await internshipLocationsService
        .getInternshipLocationById(params)
        .then((res) => {
          dispatch(loadSelectedInternshipLocationData(res));
        })
        .catch((err) => {
          notifyError(err.message);
          navigate('/cadastros/locais-de-estagio');
        })
        .finally(() => {
          changeRequestStatus('getInternshipLocationById', STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestUpdateInternshipLocation = useCallback(
    async (params: UpdateInternshipLocationUseCaseInterface.Params) => {
      changeRequestStatus('createOrUpdate', STATUSPAGE.LOADING);

      await internshipLocationsService
        .updateInternshipLocation(params)
        .then((res) => {
          requestGetInternshipLocationById({ id: params.id });
          notifySuccess(res.message);
        })
        .catch((err) => {
          notifyError(err.message);
        })
        .finally(() => {
          changeRequestStatus('createOrUpdate', STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestUpdateInternshipLocationImage = useCallback(
    (params: UpdateInternshipLocationImageUseCaseInterface.Params) => {
      changeRequestStatus('updateInternshipLocationImage', STATUSPAGE.LOADING);
      internshipLocationsService
        .updateInternshipLocationImage(params)
        .then((res) => {
          setPhotoFilePreviewUrl(params.photo);
          notifySuccess(res.message);
        })
        .catch((err) => {
          setPhotoFilePreviewUrl('');
          if (photoFileInputRef?.current) {
            photoFileInputRef.current.value = '';
          }
          notifyError(err?.message || 'Algo inesperado aconteceu');
        })
        .finally(() => {
          changeRequestStatus('updateInternshipLocationImage', STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestDeleteInternshipLocationImage = useCallback(
    (params: DeleteInternshipLocationImageUseCaseInterface.Params) => {
      changeRequestStatus('deleteInternshipLocationImage', STATUSPAGE.LOADING);
      internshipLocationsService
        .deleteInternshipLocationImage(params)
        .then((res) => {
          toggleConfirmDeleteImageModal();
          setPhotoFilePreviewUrl('');
          if (photoFileInputRef?.current) {
            photoFileInputRef.current.value = '';
          }
          notifySuccess(res.message);
        })
        .catch((err) => {
          notifyError(err?.message || 'Algo inesperado aconteceu');
        })
        .finally(() => {
          changeRequestStatus('deleteInternshipLocationImage', STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestGetAllCitiesForSelectOptions = useCallback(
    async (params: GetAllCitiesForSelectOptionsUseCaseInterface.Params) => {
      changeRequestStatus('getAllCitiesForSelectOptions', STATUSPAGE.LOADING);
      await locationsService
        .getAllCitiesForSelectOptions(params)
        .then((res) => setCitiesForSelectOptionsData(res))
        .catch(() => {
          setCitiesForSelectOptionsData([]);
          console.warn('Não foi possível listar as cidades');
        })
        .finally(() =>
          changeRequestStatus('getAllCitiesForSelectOptions', STATUSPAGE.DONE),
        );
    },
    [],
  );

  const requestGetCoordinatesFromAddress = useCallback(
    async (
      params: GetCoordinatesFromAddressUseCaseInterface.Params,
    ): Promise<{ lat: number | null; lng: number | null }> => {
      changeRequestStatus('getCoordinatesFromAddress', STATUSPAGE.LOADING);

      return locationsService
        .getCoordinatesFromAddress(params)
        .then((res) => {
          const { lat, lng } = res;

          return {
            lat,
            lng,
          };
        })
        .catch(() => {
          return {
            lat: null,
            lng: null,
          };
        })
        .finally(() =>
          changeRequestStatus('getCoordinatesFromAddress', STATUSPAGE.DONE),
        );
    },
    [],
  );
  // Form Validation
  const validation = useFormik<CreateOrUpdateInternshipLocationFormStructure>({
    enableReinitialize: true,
    initialValues: getCreateOrUpdateInternshipLocationFormInitialValues(
      selectedInternshipLocationData,
    ),
    validationSchema: getCreateOrUpdateInternshipLocationFormValidationSchema(),
    onSubmit: async (values) => {
      try {
        const {
          country,
          nonBrazilianState,
          nonBrazilianCity,
          city,
          street,
          zipCode,
          district,
          cnpjOrCpf,
          addressNumber,
          internshipResponsible,
          ...restParams
        } = values;

        const countryId = country?.value;
        const isBrazil = !country || countryId === 'BR';
        const [cityName, stateName] = isBrazil
          ? city
            ? city.label.split(' - ')
            : ['', '']
          : [nonBrazilianCity, nonBrazilianState];

        const hasChangedCountry =
          values.country.value !==
          (selectedInternshipLocationData?.country?.id ?? '');
        const hasChangedNonBrazilianState =
          values.nonBrazilianState !==
          (selectedInternshipLocationData?.nonBrazilianState ?? '');
        const hasChangedNonBrazilianCity =
          values.nonBrazilianCity !==
          (selectedInternshipLocationData?.nonBrazilianCity ?? '');
        const hasChangedCity =
          values.city?.value !== selectedInternshipLocationData?.city?.id;
        const hasChangedStreet =
          values.street !== selectedInternshipLocationData?.street;
        const hasChangedDistrict =
          values.district !== selectedInternshipLocationData?.district;
        const hasChangedAddressNumber =
          values.addressNumber !==
          selectedInternshipLocationData?.addressNumber;
        const hasChangedZipCode = isBrazil
          ? removeZipCodeMask(values.zipCode) !==
            removeZipCodeMask(selectedInternshipLocationData?.zipCode)
          : values.nonBrazilianZipCode !==
            (selectedInternshipLocationData?.zipCode ?? '');

        const hasChangedAddressFields = id
          ? hasChangedCountry ||
            hasChangedNonBrazilianState ||
            hasChangedNonBrazilianCity ||
            hasChangedCity ||
            hasChangedStreet ||
            hasChangedDistrict ||
            hasChangedAddressNumber ||
            hasChangedZipCode
          : false;

        const coordinates =
          street && (id ? hasChangedAddressFields : true)
            ? await requestGetCoordinatesFromAddress({
                street,
                addressNumber,
                country: country?.label,
                state: stateName,
                city: cityName,
                zipCode,
                district,
              })
            : id
              ? {
                  lat: selectedInternshipLocationData?.lat,
                  lng: selectedInternshipLocationData?.lng,
                }
              : undefined;

        const commonValuesToSubmit = {
          countryId: countryId || undefined,
          nonBrazilianState: nonBrazilianState || undefined,
          nonBrazilianCity: nonBrazilianCity || undefined,
          street,
          zipCode: isBrazil ? zipCode : values.nonBrazilianZipCode,
          district,
          addressNumber,
          cityId: isBrazil ? Number(city?.value) : undefined,
          lat: coordinates?.lat,
          lng: coordinates?.lng,
          cnpjOrCpf: cnpjOrCpf ? cnpjOrCpf : null,
          internshipResponsible: {
            cpf: removeCpfMask(internshipResponsible?.cpf),
            name: internshipResponsible?.name,
            role: internshipResponsible?.role,
            phone: removeTelephoneCountryCode(internshipResponsible?.phone),
            email: internshipResponsible?.email,
          },
          ...restParams,
        };

        if (id) {
          requestUpdateInternshipLocation({
            id,
            ...commonValuesToSubmit,
          });
        } else {
          requestCreateInternshipLocation(commonValuesToSubmit);
        }
      } catch (error) {}
    },
  });

  //
  const handleAddOrEditMarkerLocationBtnClick = useCallback(
    async (isEdit: boolean) => {
      const {
        country,
        nonBrazilianState,
        nonBrazilianCity,
        city,
        street,
        zipCode,
        district,
        addressNumber,
      } = selectedInternshipLocationData;

      const countryId = country?.id;
      const isBrazil = !country || countryId === 'BR';
      const [cityName, stateName] = isBrazil
        ? city
          ? [city.name, city.federalUnit]
          : ['', '']
        : [nonBrazilianCity, nonBrazilianState];

      const coordinates = isEdit
        ? {
            lat: selectedInternshipLocationData?.lat,
            lng: selectedInternshipLocationData?.lng,
          }
        : street
          ? await requestGetCoordinatesFromAddress({
              country: countryId,
              state: stateName,
              city: cityName,
              street,
              zipCode,
              district,
              addressNumber,
            })
          : undefined;

      navigate(
        `/cadastros/locais-de-estagio/editar/${id}/editar-localizacao-do-marcador-no-mapa`,
        {
          state: {
            id: 'edit-marker-location',
            value: {
              id,
              lat: coordinates?.lat,
              lng: coordinates?.lng,
              countryId: countryId,
            },
          },
        },
      );
    },
    [
      selectedInternshipLocationData?.country,
      selectedInternshipLocationData?.nonBrazilianState,
      selectedInternshipLocationData?.nonBrazilianCity,
      selectedInternshipLocationData?.zipCode,
      selectedInternshipLocationData?.street,
      selectedInternshipLocationData?.district,
      selectedInternshipLocationData?.city,
      selectedInternshipLocationData?.addressNumber,
    ],
  );

  const clearAddressInputValues = useCallback(() => {
    validation.setFieldValue('street', '');
    validation.setFieldValue('district', '');
    validation.setFieldValue('city', null);
    validation.setFieldValue('addressNumber', '');
  }, [validation]);

  const resetInputsOnCountryChange = useCallback(
    (countryId: string | null) => {
      if (countryId === 'BR' || !countryId) {
        validation.setFieldValue('nonBrazilianZipCode', '');
        validation.setFieldValue('nonBrazilianState', '');
        validation.setFieldValue('nonBrazilianCity', '');
        toast.info('Campos de endereço foram ajustados para o Brasil.');
        return;
      }
      toast.info('Campos de endereço foram ajustados para modo Internacional.');
      validation.setFieldValue('zipCode', '');
      validation.setFieldValue('city', null);
    },
    [validation],
  );

  const autoFillAddressInputValuesAccordinglyToZipCodeAPI = useCallback(
    ({
      logradouro,
      bairro,
      ibge,
      localidade,
    }: GetAddressViaZipCodeUseCaseInterface.Result) => {
      validation.setFieldValue('street', logradouro || '');
      validation.setFieldValue('district', bairro || '');
      validation.setFieldValue(
        'city',
        localidade && ibge
          ? {
              label: localidade,
              value: Number(ibge),
            }
          : null,
      );
    },
    [],
  );

  // Request Search By CNPJ

  const requestSearchByCnpj = useCallback(
    (params: GetSearchByCnpjUseCaseInterface.Params) => {
      searchByCnpjService
        .searchCnpj(params)
        .then((res) => {
          autoFillAddressInputValuesToCnpjAPI(res);
        })
        .catch((err) => {
          notifyError(err.message || 'Algo inesperado aconteceu');
        });
    },
    [],
  );

  const requestGetAddressViaZipCode = useCallback(
    (params: GetAddressViaZipCodeUseCaseInterface.Params) => {
      if (!params) return;
      changeRequestStatus('getAddressViaZipCode', STATUSPAGE.LOADING);
      locationsService
        .getAddressViaZipCode(params)
        .then((res) => {
          if (res.erro) {
            clearAddressInputValues();
            throw new Error('CEP inexistente');
          } else {
            autoFillAddressInputValuesAccordinglyToZipCodeAPI(res);
          }
        })
        .catch((err) => {
          notifyError(err.message || 'Algo inesperado aconteceu');
        })
        .finally(() =>
          changeRequestStatus('getAddressViaZipCode', STATUSPAGE.DONE),
        );
    },
    [],
  );

  const verifyInternshipResponsibleCpf = useCallback(
    (params: VerifyInternshipResponsibleCpfUseCaseInterface.Params) => {
      changeRequestStatus('verifyInternshipResponsibleCpf', STATUSPAGE.LOADING);
      internshipLocationsService
        .verifyInternshipResponsibleCpf(params)
        .then((res) => {
          if (validation.errors?.internshipResponsible?.cpf) {
            validation.setFieldError('internshipResponsible.cpf', undefined);
          }
          autoFillInternshipResponsibleFields(res);
        })
        .catch((err) => {
          if (err instanceof NotFoundError) {
            if (validation.errors?.internshipResponsible?.cpf) {
              validation.setFieldError('internshipResponsible.cpf', undefined);
            }
            return;
          } else if (err instanceof ConflictError) {
            validation.setFieldError(
              'internshipResponsible.cpf',
              'Não é possível utilizar este CPF',
            );
            return notifyError(
              err?.message || 'Não é possível utilizar este CPF',
            );
          }

          notifyError(
            err?.message || 'ALgo de errado aconteceu. Tente novamente.',
          );
        })
        .finally(() =>
          changeRequestStatus(
            'verifyInternshipResponsibleCpf',
            STATUSPAGE.DONE,
          ),
        );
    },
    [],
  );

  const verifyInternshipResponsibleEmail = useCallback(
    (params: VerifyInternshipResponsibleEmailUseCaseInterface.Params) => {
      changeRequestStatus(
        'verifyInternshipResponsibleEmail',
        STATUSPAGE.LOADING,
      );
      internshipLocationsService
        .verifyInternshipResponsibleEmail(params)
        .then((res) => {
          if (validation.errors?.internshipResponsible?.email) {
            validation.setFieldError('internshipResponsible.email', undefined);
          }
        })
        .catch((err) => {
          if (err instanceof NotFoundError) {
            if (validation.errors?.internshipResponsible?.email) {
              validation.setFieldError(
                'internshipResponsible.email',
                undefined,
              );
            }
            return;
          } else if (err instanceof ConflictError) {
            validation.setFieldError(
              'internshipResponsible.email',
              'Não é possível utilizar este email',
            );
            return notifyError(
              err?.message || 'Não é possível utilizar este email',
            );
          }

          notifyError(
            err?.message || 'Algo de errado aconteceu. Tente novamente.',
          );
        })
        .finally(() =>
          changeRequestStatus(
            'verifyInternshipResponsibleEmail',
            STATUSPAGE.DONE,
          ),
        );
    },
    [],
  );

  const autoFillInternshipResponsibleFields = useCallback(
    (
      params: Partial<
        CreateOrUpdateInternshipLocationFormStructure['internshipResponsible']
      >,
    ) => {
      validation.setFieldValue('internshipResponsible.name', params.name || '');
      validation.setFieldValue('internshipResponsible.role', params.role || '');
      validation.setFieldValue(
        'internshipResponsible.phone',
        params.phone ? `${'+55' + params.phone}` : '',
      );
      validation.setFieldValue(
        'internshipResponsible.email',
        params.email || '',
      );
    },
    [],
  );

  return {
    id,
    cpfSchema,
    validation,
    emailSchema,
    zipCodeSchema,
    requestsStatus,
    photoFileInputRef,
    photoFilePreviewUrl,
    areInputsAndBtnsDisabled,
    countriesData,
    citiesForSelectOptionsData,
    isConfirmDeleteImageModalOpen,
    selectedInternshipLocationData,
    navigate,
    getFormattedAddress,
    requestSearchByCnpj,
    setPhotoFilePreviewUrl,
    handlePhotoInputChange,
    requestGetAllCountries,
    requestGetAddressViaZipCode,
    setCitiesForSelectOptionsData,
    verifyInternshipResponsibleCpf,
    requestCreateInternshipLocation,
    requestUpdateInternshipLocation,
    verifyInternshipResponsibleEmail,
    requestGetInternshipLocationById,
    handleAddOrEditMarkerLocationBtnClick,
    toggleConfirmDeleteImageModal,
    resetInputsOnCountryChange,
    requestGetAllCitiesForSelectOptions,
    removeSelectedInternshipLocationData,
    requestUpdateInternshipLocationImage,
    requestDeleteInternshipLocationImage,
    requestGetCoordinatesFromAddress,
  };
};
